import { Delaunay } from 'd3';
import type {
  GridResolution,
  NumericRange,
  ScalarGrid,
  XyzDataPoint,
  ZInterpolation,
} from '../types';
import { toScalarGrid } from '../data/toScalarGrid';
import { toLatticeFrame } from '../data/horizontalAxes';
import { sampleLattice } from '../data/sampleLattice';
import { toCssColor, type Rgb } from './colorScale';
import { parseColor, toInkedColor } from './panelShading';

/**
 * What colors the surface.
 *
 * - `height`: the sequential ramp over z.
 * - `gradient`: a second numeric field, interpolated across the plane the way z is, components at
 *   one position averaged whatever the height's aggregation, and looked up
 *   in `colors` - evenly spaced samples of a continuous scale over `domain`, `null` where the scale
 *   gives no color.
 * - `regions`: one color per component, each painting the part of the plane nearest to it, for
 *   colors that name a category and cannot be blended.
 *
 * Components missing from the map take `unknownColor`.
 */
export type SurfaceColoring =
  | { kind: 'height' }
  | {
      kind: 'gradient';
      valuesById: ReadonlyMap<string, number>;
      domain: NumericRange;
      colors: (string | null)[];
    }
  | { kind: 'regions'; colorsById: ReadonlyMap<string, string> };

/** An RGBA image over the plot square, row 0 at cube y = -1, sampled in place of the ramp. */
export type SurfacePaint = {
  width: number;
  height: number;
  data: Uint8ClampedArray;
};

const UNKNOWN_PAINT_GRAY: Rgb = [158, 158, 158];
const UNKNOWN_PAINT_FADE = 0.5;

/** Where the coloring gives no color: a neutral gray faded halfway into the chart's surface. */
export const toUnknownPaintColor = (surfaceColor: string): Rgb =>
  toInkedColor(
    UNKNOWN_PAINT_GRAY,
    parseColor(surfaceColor) ?? UNKNOWN_PAINT_GRAY,
    UNKNOWN_PAINT_FADE
  );

/**
 * The same color as CSS, mixed against a color the stylesheet resolves - a design token's
 * `var(...)` - for a host that has no resolved surface color to hand.
 */
export const toUnknownPaintCss = (surfaceCss: string): string =>
  `color-mix(in srgb, ${toCssColor(UNKNOWN_PAINT_GRAY)} ${(1 - UNKNOWN_PAINT_FADE) * 100}%, ${surfaceCss})`;

/** Texels along each side of the paint. */
export const SURFACE_PAINT_SIZE = 512;

type PaintArgs = {
  coloring: SurfaceColoring;
  points: XyzDataPoint[];
  /** The height grid with the host's axes, whose square the paint covers. */
  grid: ScalarGrid;
  interpolation: ZInterpolation;
  resolution: GridResolution;
  /** Whether the height grid was overfilled for clipping, so a gradient's field matches it. */
  overfillForClipping: boolean;
  unknownColor: Rgb;
  size: number;
};

const toCubeCoordinate = (texel: number, size: number): number =>
  ((texel + 0.5) / size) * 2 - 1;

const fill = (
  size: number,
  colorAt: (cubeX: number, cubeY: number) => Rgb
): SurfacePaint => {
  const data = new Uint8ClampedArray(size * size * 4);
  for (let row = 0; row < size; row++) {
    const cubeY = toCubeCoordinate(row, size);
    for (let column = 0; column < size; column++) {
      const [red, green, blue] = colorAt(toCubeCoordinate(column, size), cubeY);
      const at = (row * size + column) * 4;
      data[at] = red;
      data[at + 1] = green;
      data[at + 2] = blue;
      data[at + 3] = 255;
    }
  }
  return { width: size, height: size, data };
};

const parseColors = <K>(colors: ReadonlyMap<K, string>): Map<K, Rgb> =>
  new Map(
    [...colors].flatMap(([key, css]) => {
      const rgb = parseColor(css);
      return rgb ? [[key, rgb] as const] : [];
    })
  );

/**
 * A gradient coloring's color for a value: the nearest of its samples, clamped to its domain, or
 * null where the scale gives none.
 */
export const toGradientLookup = (
  [low, high]: NumericRange,
  colors: (string | null)[]
): ((value: number) => Rgb | null) => {
  const lookup = colors.map(css => (css === null ? null : parseColor(css)));
  const span = high - low;
  return value => {
    if (lookup.length === 0) return null;
    const fraction = span > 0 ? (value - low) / span : 0;
    const index = Math.round(
      Math.min(1, Math.max(0, fraction)) * (lookup.length - 1)
    );
    return lookup[index] ?? null;
  };
};

const toGradientPaint = (
  {
    valuesById,
    domain,
    colors,
  }: Extract<SurfaceColoring, { kind: 'gradient' }>,
  {
    points,
    grid,
    interpolation,
    resolution,
    overfillForClipping,
    unknownColor,
    size,
  }: PaintArgs
): SurfacePaint => {
  const valued = points.flatMap(point => {
    const value = valuesById.get(point.componentId);
    return value === undefined ? [] : [{ ...point, z: value }];
  });
  const field = toScalarGrid({
    points: valued,
    interpolation,
    aggregation: 'mean',
    resolution,
    overfillForClipping,
    extent: { dataXRange: grid.dataXRange, dataYRange: grid.dataYRange },
  });
  if (field.width !== grid.width || field.height !== grid.height) {
    return fill(size, () => unknownColor);
  }

  const { toCubeX, toCubeY } = toLatticeFrame(grid);
  const cubeX0 = toCubeX(0);
  const cubeXSpan = toCubeX(grid.width - 1) - cubeX0 || 1;
  const cubeY0 = toCubeY(0);
  const cubeYSpan = toCubeY(grid.height - 1) - cubeY0 || 1;
  const colorOf = toGradientLookup(domain, colors);

  return fill(size, (cubeX, cubeY) => {
    const gridX = ((cubeX - cubeX0) / cubeXSpan) * (grid.width - 1);
    const gridY = ((cubeY - cubeY0) / cubeYSpan) * (grid.height - 1);
    const isOnLattice =
      gridX >= 0 &&
      gridY >= 0 &&
      gridX <= grid.width - 1 &&
      gridY <= grid.height - 1;
    const value = isOnLattice
      ? sampleLattice(field, gridX, gridY, 'nearest')
      : null;
    return (value === null ? null : colorOf(value)) ?? unknownColor;
  });
};

/**
 * One point per position for the regions to grow from, a colored component kept over an uncolored
 * one sharing its position. A triangulation keeps only the first point at a position, so a gray
 * component listed first would hide a colored one beneath it.
 */
const toRegionSites = (
  points: XyzDataPoint[],
  colors: Map<string, Rgb>
): XyzDataPoint[] => [
  ...points
    .reduce((sites, point) => {
      const key = `${point.x}|${point.y}`;
      const existing = sites.get(key);
      const keepsExisting =
        existing !== undefined &&
        (colors.has(existing.componentId) || !colors.has(point.componentId));
      return keepsExisting ? sites : sites.set(key, point);
    }, new Map<string, XyzDataPoint>())
    .values(),
];

const toRegionPaint = (
  { colorsById }: Extract<SurfaceColoring, { kind: 'regions' }>,
  { points, grid, unknownColor, size }: PaintArgs
): SurfacePaint => {
  if (points.length === 0) return fill(size, () => unknownColor);

  const colors = parseColors(colorsById);
  const sites = toRegionSites(points, colors);
  const delaunay = Delaunay.from(
    sites,
    ({ x }) => x,
    ({ y }) => y
  );
  const [xLow, xHigh] = grid.xRange;
  const [yLow, yHigh] = grid.yRange;
  const nearest = { index: 0 };

  return fill(size, (cubeX, cubeY) => {
    nearest.index = delaunay.find(
      xLow + ((cubeX + 1) / 2) * (xHigh - xLow),
      yLow + ((cubeY + 1) / 2) * (yHigh - yLow),
      nearest.index
    );
    return colors.get(sites[nearest.index].componentId) ?? unknownColor;
  });
};

/** The paint for a coloring, or null when the surface is colored by height. */
export const toSurfacePaint = (args: PaintArgs): SurfacePaint | null => {
  const { coloring } = args;
  if (coloring.kind === 'gradient') return toGradientPaint(coloring, args);
  if (coloring.kind === 'regions') return toRegionPaint(coloring, args);
  return null;
};

/**
 * The paint's color at a point of the plot square, 0 to 255 per channel, filtered the way the GPU's linear sampler with
 * clamped edges filters it, so both renderers draw the same thing.
 */
export const samplePaint = (
  { width, height, data }: SurfacePaint,
  cubeX: number,
  cubeY: number,
  into: [number, number, number]
): void => {
  const texelX = Math.min(
    width - 1,
    Math.max(0, ((cubeX + 1) / 2) * width - 0.5)
  );
  const texelY = Math.min(
    height - 1,
    Math.max(0, ((cubeY + 1) / 2) * height - 0.5)
  );
  const column = Math.min(width - 2, Math.floor(texelX));
  const row = Math.min(height - 2, Math.floor(texelY));
  const tx = texelX - column;
  const ty = texelY - row;
  const lowLeft = (row * width + column) * 4;
  const highLeft = lowLeft + width * 4;

  for (let channel = 0; channel < 3; channel++) {
    const low =
      data[lowLeft + channel] +
      (data[lowLeft + 4 + channel] - data[lowLeft + channel]) * tx;
    const high =
      data[highLeft + channel] +
      (data[highLeft + 4 + channel] - data[highLeft + channel]) * tx;
    into[channel] = low + (high - low) * ty;
  }
};
